import { HSpacer, InfoTooltip, Select, Text, VSpacer } from "@/components/DesignSystem";
import { QueryKeys } from '@/constants/QueryKeys';
import { SubwalletApi } from '@/utilities/api/SubwalletApi';
import { ApiPromotion, ApiRetailer } from "@api/interfaces";
import { CircularProgress, MenuItem, Stack } from "@mui/material";
import { SponsorType } from '@shared/enums';
import React from "react";
import { useQuery } from 'react-query';

interface SubwalletInputSelectorBaseProps {
  disabled?: boolean,
  onChange: (subwalletId: string) => void,
  selectedSubwalletId?: string | null,
}

type SubwalletInputSelectorPromotionProps = SubwalletInputSelectorBaseProps & {
  promotion: ApiPromotion,
  retailer?: never,
};

type SubwalletInputSelectorRetailerProps = SubwalletInputSelectorBaseProps & {
  promotion?: never,
  retailer: ApiRetailer,
};

type SubwalletInputSelectorProps =
  | SubwalletInputSelectorPromotionProps
  | SubwalletInputSelectorRetailerProps;

export const SubwalletInputSelector = ({
  disabled,
  onChange,
  promotion,
  retailer,
  selectedSubwalletId,
}: SubwalletInputSelectorProps) => {
  const retailerId = (promotion?.retailerId ?? retailer?.id) as string;
  const isForRewardsProgram = !!retailer;
  const isDisabled = disabled || (promotion?.sponsorType === SponsorType.Manufacturer
    && !promotion.manufacturerId);

  const { data: subwallets, isFetching: isFetchingSubwallets } = useQuery(
    [QueryKeys.LIST_SUBWALLETS, retailerId, promotion?.manufacturerId],
    () => SubwalletApi.list({
      retailerId,
      manufacturerId: promotion?.manufacturerId || null,
    }),
  );

  if (isFetchingSubwallets) {
    return (
      <Stack alignItems="center">
        <CircularProgress />
      </Stack>
    );
  }

  subwallets?.sort((a, b) =>
    (a.manufacturerId != null ? 1 : 0) - (b.manufacturerId != null ? 1 : 0) ||
    a.name.localeCompare(b.name),
  );
  const infoText = isForRewardsProgram
    ? 'This is the subwallet that all rewards program earned points will be deposited in.'
    : 'This is the subwallet that all earned points from this promotion will be deposited in.';

  return (
    <>
      <Stack alignItems="center" direction="row">
        <Text category="title-medium">
          Subwallet
        </Text>
        <HSpacer size="3"/>
        <InfoTooltip>
          {infoText}
        </InfoTooltip>
      </Stack>
      <VSpacer size="4"/>
      <Select
        disabled={isDisabled}
        label="Select a subwallet"
        onChangeValue={onChange}
        testID="subwallet-select"
        value={selectedSubwalletId}
      >
        {subwallets?.map((subwallet) => (
          <MenuItem key={subwallet.id} value={subwallet.id}>
            {subwallet.name}
          </MenuItem>
        ))}
      </Select>
      <VSpacer size="8"/>
    </>
  );
};
